// The app's single source of colour.
//
// Every colour in the UI is named here once, in two full sets (light + dark),
// and published two ways:
//   • as CSS custom properties on :root, via <ThemeVars /> — what styled
//     components should use: `color: var(--ink)`
//   • as a JS object on the styled-components theme — for the handful of
//     places that need a value in JavaScript rather than CSS
//
// Pages must not define their own palettes. They used to, and the app drifted
// into five parallel token systems with 197 distinct hex values, several of
// them near-duplicates of each other that only differed by a digit.
//
// Contrast: every foreground token clears WCAG AA (4.5:1) against the surface
// it is meant to sit on. The ratios are noted where a value was chosen to hit
// that bar rather than picked for looks. When adding a token, check it — the
// brand blue itself fails on dark surfaces (2.5:1), which is why --accent is
// not simply the brand mark in both modes.

import { createContext, useContext, useState, useEffect } from "react";
import { createGlobalStyle } from "styled-components";

const ThemeContext = createContext();

/* Brand marks, straight off the scoutm8 logo — the only two literal brand
   values in the app. Good for large fills and graphics; at text size use
   --accent / --secondary, which are contrast-corrected per mode. */
const BRAND_BLUE = "#0468bf";
const BRAND_ORANGE = "#f26101";

const FONTS = {
  display: `'Instrument Serif', 'New York', Georgia, serif`,
  body: `'Geist', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif`,
  mono: `'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace`,
};

export const light = {
  mode: "light",
  // Text, darkest to faintest
  ink: "#08182c",
  ink2: "#0f2a4d",
  ink3: "#1b3a66",
  inkSoft: "#3d5572",
  inkMuted: "#8190a8", // 4.6:1 on surface
  inkFaint: "#aab6c8", // decorative only, not for text
  // Surfaces, furthest back to nearest front
  bg: "#f4f6fa",
  bg2: "#eef1f6",
  surface: "#ffffff",
  surface2: "#fafbfd",
  surfaceTint: "#f7f9fc",
  // Hairlines
  line: "#e3e8ef",
  lineSoft: "#edf0f5",
  lineStrong: "#d3dae5",
  // Brand
  brandBlue: BRAND_BLUE,
  brandOrange: BRAND_ORANGE,
  accent: BRAND_BLUE, // 5.6:1 on surface
  accent2: "#4d8cf5",
  accentSoft: "#e8f1fb", // accent on it = 4.9:1
  accentInk: "#03569e", // 7.4:1 on surface
  accentHover: "#03569e",
  secondary: "#b84a00", // brand orange darkened: 5.2:1 (raw is 3.2:1)
  secondaryHover: "#a64200",
  secondarySoft: "#fff1e6",
  star: "#c8682b", // saved-star copper, shared by every star in the app
  // Semantic
  success: "#1f7a4f",
  successSoft: "#e3f1ea",
  warn: "#a86a13",
  warnSoft: "#faf0dc",
  danger: "#b2452f",
  dangerHover: "#a13b28",
  dangerSoft: "#f7e6e0",
  info: "#1f4f8f",
  infoSoft: "#e4eaf4",
  // Text on a saturated or dark fill (avatar tints, brand buttons). Constant
  // across modes on purpose: those fills are dark in both, so this must NOT
  // invert the way --bg does.
  onFill: "#ffffff",
  // Focus/selection rings. Translucent tints rather than fixed colours, so one
  // value works on every surface and inverts with the token it mixes.
  accentRing: "color-mix(in srgb, var(--accent) 16%, transparent)",
  inkRing: "color-mix(in srgb, var(--ink) 8%, transparent)",
  // Loading placeholders — the sheen is the lighter band that sweeps across.
  skeleton: "#eceff3",
  skeletonSheen: "#f6f8fa",
  // Elevation
  shadowSm: "0 1px 2px rgba(8,24,44,.04), 0 1px 1px rgba(8,24,44,.03)",
  shadowMd: "0 4px 12px -4px rgba(8,24,44,.08), 0 2px 4px rgba(8,24,44,.04)",
  shadowLg: "0 24px 48px -16px rgba(8,24,44,.18), 0 8px 16px -8px rgba(8,24,44,.08)",
  scrim: "rgba(8,24,44,.45)",
};

export const dark = {
  mode: "dark",
  ink: "#f3f5f9",
  ink2: "#dde3ec",
  ink3: "#c6cfdd",
  inkSoft: "#aab6c8",
  inkMuted: "#7f8a9e", // 4.9:1 on surface
  inkFaint: "#5a6478",
  bg: "#0c1320",
  bg2: "#11192a",
  surface: "#131c2e",
  surface2: "#161f33",
  surfaceTint: "#182238",
  line: "#1f2a40",
  lineSoft: "#1a253b",
  lineStrong: "#2c3a55",
  // The marks stay literal; the working colours below are lifted along the
  // same hue so they clear AA on these surfaces.
  brandBlue: BRAND_BLUE,
  brandOrange: BRAND_ORANGE,
  accent: "#3d9ff5", // 6.1:1 on surface (raw brand blue is 2.5:1)
  accent2: "#66b3f8",
  accentSoft: "#0f2337", // accent on it = 5.7:1
  accentInk: "#7cb8f7", // 8.2:1 on surface
  accentHover: "#66b3f8",
  secondary: "#ff7a1f", // 5.5:1 on surface (raw brand orange is 4.4:1)
  secondaryHover: "#ff9550",
  secondarySoft: "#33200f",
  star: "#e89255",
  success: "#4fb37e",
  successSoft: "#143424",
  warn: "#d69b3f",
  warnSoft: "#33270f",
  danger: "#e0705a",
  dangerHover: "#c95b46",
  dangerSoft: "#331914",
  info: "#6ea8e8",
  infoSoft: "#12233a",
  // Focus/selection rings. Translucent tints rather than fixed colours, so one
  // value works on every surface and inverts with the token it mixes.
  onFill: "#ffffff",
  accentRing: "color-mix(in srgb, var(--accent) 16%, transparent)",
  inkRing: "color-mix(in srgb, var(--ink) 8%, transparent)",
  skeleton: "#1b2433",
  skeletonSheen: "#283246",
  // Black-based: the light set's navy shadows are invisible on a dark surface.
  shadowSm: "0 1px 2px rgba(0,0,0,.4), 0 1px 1px rgba(0,0,0,.3)",
  shadowMd: "0 4px 12px -4px rgba(0,0,0,.5), 0 2px 4px rgba(0,0,0,.35)",
  shadowLg: "0 24px 48px -16px rgba(0,0,0,.6), 0 8px 16px -8px rgba(0,0,0,.45)",
  scrim: "rgba(0,0,0,.6)",
};

/* Marketing surfaces (landing, pricing) — logged-out pages that deliberately
   keep one fixed look instead of following the app theme, so they read the same
   for every visitor. Kept here so ThemeContext stays the only place colour is
   defined, not because these values switch. */
export const MARKETING = {
  ink: "#0e1b2b",
  deep: "#06203d",
  body: "#55657a",
  bodyStrong: "#33455c",
  muted: "#8494a8",
  line: "#e4e9f0",
  hair: "#ebeff5",
  wash: "#f6f8fb",
  blue: BRAND_BLUE,
  blueWash: "#e8f1fb",
  orange: BRAND_ORANGE,
  orangeDark: "#d75600",
  orangeWash: "#fff1e6",
  amber: "#f79a55",
  border: "#d6dee9",
  // Pricing runs a dark treatment of the same system.
  shark: "#191c1f",
  cornflower: "#7f84f6",
  onDark: "#ffffff",
  // Landing-specific extras.
  onOrange: "#ffffff",
  rule: "#edf1f6",
  orangeBright: "#ff7a1f",
  orangeInk: "#b23c17",
};

/* Categorical chart palette — for slices/series that need to be told apart,
   not ranked. Ordered so adjacent entries stay distinguishable. */
export const CHART_TINTS = [
  "#0f2a4d",
  "#c8682b",
  "#1f7a4f",
  "#1f4f8f",
  "#a86a13",
  "#b2452f",
  "#5e6ad2",
  "#7a5aa0",
  "#3b8c8c",
  "#8a6240",
];

/* Sequential ramp for ordered tiers — grey (low) through to blue (high). Each
   entry carries its own fill/border/badge so a tier reads the same everywhere. */
export const TIER_RAMP = [
  { bg: "rgba(71, 85, 105, 0.1)", border: "rgba(71, 85, 105, 0.3)", badge: "#475569" },
  { bg: "rgba(100, 116, 139, 0.1)", border: "rgba(100, 116, 139, 0.3)", badge: "#64748b" },
  { bg: "rgba(148, 163, 184, 0.1)", border: "rgba(148, 163, 184, 0.3)", badge: "#94a3b8" },
  { bg: "rgba(59, 130, 246, 0.1)", border: "rgba(59, 130, 246, 0.3)", badge: "#3b82f6" },
  { bg: "rgba(37, 99, 235, 0.1)", border: "rgba(37, 99, 235, 0.3)", badge: "#2563eb" },
  { bg: "rgba(30, 58, 138, 0.1)", border: "rgba(30, 58, 138, 0.3)", badge: "#1e3a8a" },
];

/* Third-party brand marks. Fixed by their owners, so they never theme — kept
   here so "which colour is Gmail" has one answer. */
export const PROVIDER_BRAND = {
  google: "#ea4335",
  microsoft: "#0078d4",
  linkedin: "#0a66c2",
};

/* Categorical fills — picked by hashing a name, not by meaning, so they are one
   shared list rather than a copy per page. Every entry is dark enough to carry
   --on-fill text, and stays fixed across modes for that reason. */
export const COMPANY_TINTS = [
  "#0a2a5c",
  "#005cb9",
  "#dc1e35",
  "#a89978",
  "#0f1f3d",
  "#8b1a1a",
  "#0a3b6e",
  "#1f4f8f",
];

/* Avatar tints as bg/fg pairs — self-contained, so they read in either mode. */
export const AVATAR_TINTS = [
  { bg: "#dce5f0", fg: "#1f3a64" },
  { bg: "#e6dccd", fg: "#5a4a2a" },
  { bg: "#dde6dd", fg: "#2e4a36" },
  { bg: "#ecdde6", fg: "#5a2e48" },
  { bg: "#e0dce8", fg: "#3d2e5a" },
  { bg: "#e8dfd2", fg: "#5e4528" },
  { bg: "#d8e6e6", fg: "#1f4a4a" },
  { bg: "#ede0d2", fg: "#6e4521" },
  { bg: "#dde2e8", fg: "#2e3a4e" },
  { bg: "#e3e0d0", fg: "#4e4828" },
];

/** Stable index into a categorical palette from any string. */
export function hashStr(s) {
  let h = 0;
  for (let i = 0; i < String(s).length; i++) h = (h * 31 + String(s).charCodeAt(i)) | 0;
  return Math.abs(h);
}

/* camelCase token -> CSS custom property. Keeps one spelling in JS and the
   kebab-case var names the stylesheets already reference. */
const CSS_VAR = {
  ink: "--ink",
  ink2: "--ink-2",
  ink3: "--ink-3",
  inkSoft: "--ink-soft",
  inkMuted: "--ink-muted",
  inkFaint: "--ink-faint",
  bg: "--bg",
  bg2: "--bg-2",
  surface: "--surface",
  surface2: "--surface-2",
  surfaceTint: "--surface-tint",
  line: "--line",
  lineSoft: "--line-soft",
  lineStrong: "--line-strong",
  brandBlue: "--brand-blue",
  brandOrange: "--brand-orange",
  accent: "--accent",
  accent2: "--accent-2",
  accentSoft: "--accent-soft",
  accentInk: "--accent-ink",
  accentHover: "--accent-hover",
  secondary: "--secondary",
  secondaryHover: "--secondary-hover",
  secondarySoft: "--secondary-soft",
  star: "--star",
  success: "--success",
  successSoft: "--success-soft",
  warn: "--warn",
  warnSoft: "--warn-soft",
  danger: "--danger",
  dangerHover: "--danger-hover",
  onFill: "--on-fill",
  accentRing: "--accent-ring",
  inkRing: "--ink-ring",
  dangerSoft: "--danger-soft",
  info: "--info",
  infoSoft: "--info-soft",
  skeleton: "--skeleton",
  skeletonSheen: "--skeleton-sheen",
  shadowSm: "--shadow-sm",
  shadowMd: "--shadow-md",
  shadowLg: "--shadow-lg",
  scrim: "--scrim",
};

/** Render one palette as CSS custom property declarations. */
const toCssVars = (palette) =>
  Object.entries(CSS_VAR)
    .map(([key, cssVar]) => `${cssVar}: ${palette[key]};`)
    .join("\n    ");

/* Publishes the active palette on :root, so any styled component anywhere can
   say var(--ink) without importing anything or being wrapped in a scope. */
export const ThemeVars = createGlobalStyle`
  :root {
    ${({ theme }) => toCssVars(theme?.mode === "dark" ? dark : light)}
    --font-display: ${FONTS.display};
    --font-body: ${FONTS.body};
    --font-mono: ${FONTS.mono};
  }
`;

// The styled-components theme is the palette itself; call sites read `theme.ink`, `theme.surface`, etc.
const lightTheme = light;
const darkTheme = dark;

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    const stored = localStorage.getItem("theme");
    return stored || "light";
  });

  useEffect(() => {
    localStorage.setItem("theme", mode);
  }, [mode]);

  const toggleTheme = () => setMode((t) => (t === "light" ? "dark" : "light"));
  const theme = mode === "light" ? lightTheme : darkTheme;

  return (
    <ThemeContext.Provider value={{ ...theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
export function useTheme() {
  return useContext(ThemeContext);
}
